import React from "react";
import styled from "styled-components";
import { colors2, TooltipV2 } from "oolib";
import { UI_CAPTION_SEMIBOLD_DF } from "oolib/dist/v2/components/Typo2";

// Wraps the bar in a column so the legend stacks below it.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const Segments = styled.div`
  display: flex;
  gap: 2px;
  width: 100%;
  height: ${({ $height }) => $height}px;
`;

// Sized by flex-grow so the same component works whether the caller passes raw
// counts or pre-computed percentages — both are just proportional weights.
// Outer-corner rounding is decided by the caller per segment (not :first/
// :last-child) so it survives a segment being nested inside a hover group.
const Segment = styled.div`
  flex: ${({ $weight }) => $weight};
  background: ${({ $color }) => $color};
  border-radius: 2px;
  opacity: ${({ $dim }) => ($dim ? 0.3 : 1)};
  ${({ $roundLeft }) =>
    $roundLeft && "border-top-left-radius: 7px; border-bottom-left-radius: 7px;"}
  ${({ $roundRight }) =>
    $roundRight && "border-top-right-radius: 7px; border-bottom-right-radius: 7px;"}
`;

// A run of dimmed segments sharing one hover target. Same gap as Segments so
// the seams inside the run look identical to the seams outside it.
const DimmedRun = styled.div`
  display: flex;
  gap: 2px;
  width: 100%;
  height: 100%;
`;

// space-between spreads the items to the bar's ends: first sits left, last sits
// right, and any middle item centres.
const LegendContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.4rem 0.8rem;
  /* Items keep their own words on one line; the row wraps between items. */
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const LegendDot = styled.span`
  width: 8px;
  height: 12px;
  border-radius: 999px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const LegendText = styled(UI_CAPTION_SEMIBOLD_DF)`
  font-weight: 500;
  color: ${({ $color }) => $color || colors2.grey60};
  white-space: nowrap;
`;

function Legend({ color, value, count, label, textColor, suffix }) {
  return (
    <LegendItem>
      <LegendDot $color={color} />
      <LegendText $color={textColor}>{`${value}%`}</LegendText>
      {count != null && <LegendText $color={textColor}>{count}</LegendText>}
      <LegendText $color={textColor}>{label}</LegendText>
      {suffix}
    </LegendItem>
  );
}

const capitalize = (s = "") => s.charAt(0).toUpperCase() + s.slice(1);

// Group the visible segments into render items: an emphasised segment stands
// alone; each contiguous run of dimmed segments becomes one hover group.
// Without an emphasisKey every segment stands alone.
const toRenderItems = (visible, emphasisKey) => {
  if (!emphasisKey) return visible.map((s) => ({ type: "segment", segment: s }));
  const items = [];
  for (const s of visible) {
    if (s.key === emphasisKey) {
      items.push({ type: "segment", segment: s });
    } else if (items.length && items[items.length - 1].type === "run") {
      items[items.length - 1].segments.push(s);
    } else {
      items.push({ type: "run", segments: [s] });
    }
  }
  return items;
};

// The hover card over a dimmed run — the legend lines those segments would
// have had, on the tooltip's dark ground.
const DimmedRunLegend = ({ segments }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "6px 8px" }}>
    {segments.map((s) => (
      <Legend
        key={s.key}
        color={s.color}
        value={s.value}
        count={s.count}
        label={s.title || capitalize(s.key)}
        textColor={colors2.white}
      />
    ))}
  </div>
);

/**
 * Horizontal stacked sentiment/polarity bar with an optional legend below.
 *
 * @param segments   [{ key, value, color, title? }] in render order; only value > 0 are
 *                   rendered. `title` is the legend label; without it the key is capitalised.
 *                   shown, so the rounded ends line up with the actually-visible
 *                   ends.
 * @param height     bar height in px (default 5).
 * @param roundedEnds pill-rounds the outer corners of the first/last segment.
 * @param showLegend renders a dot + "{value}% [{count}] {Label}" row below the
 *                   bar (value is assumed to be a percentage; a segment's
 *                   optional `count` renders between percent and label).
 * @param emphasisKey the one segment that carries the story. When set, every
 *                   other segment dims and loses its legend entry; hovering a
 *                   dimmed run shows those entries as a tooltip instead.
 * @param emphasisLegendSuffix a node rendered after the emphasised segment's
 *                   legend label (a trend chip, say). Only meaningful with
 *                   emphasisKey.
 */
export function SentimentBar({
  segments = [],
  height = 5,
  roundedEnds = false,
  showLegend = false,
  emphasisKey = null,
  emphasisLegendSuffix = null,
}) {
  const visible = segments.filter((s) => s.value > 0);
  if (visible.length === 0) return null;

  const first = visible[0];
  const last = visible[visible.length - 1];
  const renderSegment = (s) => (
    <Segment
      key={s.key}
      $weight={s.value}
      $color={s.color}
      $dim={!!emphasisKey && s.key !== emphasisKey}
      $roundLeft={roundedEnds && s === first}
      $roundRight={roundedEnds && s === last}
    />
  );

  const legendSegments = emphasisKey ? visible.filter((s) => s.key === emphasisKey) : visible;

  return (
    <Container>
      <Segments $height={height}>
        {toRenderItems(visible, emphasisKey).map((item) =>
          item.type === "segment" ? (
            renderSegment(item.segment)
          ) : (
            <TooltipV2
              key={`run-${item.segments[0].key}`}
              primaryContent={{ layout: "custom", render: <DimmedRunLegend segments={item.segments} /> }}
              placement="bottom"
              showDelay={200}
              wrapperDisplay="block"
              wrapperStyle={{ flex: item.segments.reduce((sum, s) => sum + s.value, 0), minWidth: 0 }}
            >
              <DimmedRun>{item.segments.map(renderSegment)}</DimmedRun>
            </TooltipV2>
          )
        )}
      </Segments>
      {showLegend && (
        <LegendContainer>
          {legendSegments.map((s) => (
            <Legend
              key={s.key}
              color={s.color}
              value={s.value}
              count={s.count}
              label={s.title || capitalize(s.key)}
              suffix={emphasisKey && s.key === emphasisKey ? emphasisLegendSuffix : null}
            />
          ))}
        </LegendContainer>
      )}
    </Container>
  );
}

export default SentimentBar;
